import React, { useCallback, useMemo } from 'react';
import { useAiRovoCurrentRouteConfiguration } from '@jira/ai-rovo_actions-store/src/useAiRovoCurrentRouteConfiguration.tsx';
import { Box, xcss } from '@atlaskit/primitives';
import ConversationAssistant, {
	type ConversationAssistantConfig,
	ConversationAssistantUIModes,
} from '@atlassian/conversation-assistant';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@atlassian/jira-get-meta';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useConversationAssistantSidebarApi } from './useConversationAssistantSidebarApi.tsx';

type JiraConversationAssistantProps = {
	onClosePanel: () => void;
};

const SYNTHETIC_HEADER = 'x-issynthetic-traffic';
const noopConfiguration = {};

export const JiraConversationAssistant = ({ onClosePanel }: JiraConversationAssistantProps) => {
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const { toggleConversationAssistantUIMode, isMiniModalAvailable } = useRovoExperienceAPIStore();
	const sidebarApi = useConversationAssistantSidebarApi({});
	const { data: orgId } = useOrgId();

	const configuration = fg('global_rovo_button_in_jira_shared_layout_technical')
		? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
			useAiRovoCurrentRouteConfiguration()
		: noopConfiguration;

	const isSyntheticTenant = getMeta('ajs-is-synthetic') === 'true' || fg('is_synthetic_tenant');

	const config: ConversationAssistantConfig = useMemo(
		() => ({
			displaySubmitFlag: true,
			siteId: cloudId,
			activationId,
			product: 'jira',
			orgId,
			experienceId: 'ai-mate',
			assistanceServiceConfig: {
				// TODO: type `AssistanceServiceConfig` should be decoupled from headers to prevent the need for other required keys which shouldn't need to be defined here (`product` and `experienceId`)
				headers: isSyntheticTenant
					? { [SYNTHETIC_HEADER]: String(isSyntheticTenant) }
					: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						({} as Record<string, string>),
				// Rename to `urlPrefix` in the package; baseUrl is a deprecated concept
				baseUrl: '/gateway/api/assist',
			},
		}),
		[cloudId, activationId, orgId, isSyntheticTenant],
	);

	const modeConfig = useMemo(
		() => ({
			mode: ConversationAssistantUIModes.SIDEBAR,
			isMiniModalAvailable,
			switchToMode: (mode: ConversationAssistantUIModes | undefined) => {
				toggleConversationAssistantUIMode({ targetMode: mode, sidebarApi });
			},
		}),
		[toggleConversationAssistantUIMode, sidebarApi, isMiniModalAvailable],
	);

	const handleCloseChat = useCallback(() => {
		onClosePanel();
		toggleConversationAssistantUIMode({
			targetMode: undefined,
			sidebarApi,
		});
	}, [onClosePanel, toggleConversationAssistantUIMode, sidebarApi]);

	// ROVOFE-76 - No resize wrapper for nav4 as it uses PanelSplitter now
	return (
		<Box xcss={conversationAssistantWrapperNav4Styles}>
			<ConversationAssistant
				testId="conversation-assistant.ui.conversation-assistant"
				onCloseChat={handleCloseChat}
				config={config}
				isNav4Enabled
				{...(expValEquals('jira_ai_rovo_button', 'treatment', 'B', 'A') && {
					modeConfig,
				})}
				{...configuration}
			/>
		</Box>
	);
};

const conversationAssistantWrapperNav4Styles = xcss({
	width: '100%',
	height: '100%',
});
